import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MONGODB_AWSGW_QUERIES, MONGODB_AWSGW_APIKEY } from "../../utility/keys";
import { parseCwLogEvent, cwLevelColor } from "../../utility/cwLogParse";
import { windowMs, CW_COLUMNS } from "./logSources";
import LogEventsTable from "./LogEventsTable";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// CwLogsBrowser (#247) — CloudWatch source for the Log viewer.
//
// Fetches a time-windowed page of one Lambda's log group via the queries Lambda's
// server-side `cwLogs` proxy (FilterLogEvents). Native CloudWatch nextToken
// pagination (walk forward; a token stack backs Prev). Raw messages are parsed
// with parseCwLogEvent into { level, requestId, text, json } rows. If the Lambda
// role lacks the CloudWatch policy the proxy returns { error } (AccessDenied),
// which is shown inline — the viewer never crashes.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const PAGE_LIMIT = 300;

function CwLogsBrowser({ groupId, windowId, filterPattern }) {
  // Anchor the time window so pagination is stable across re-renders. Re-anchored
  // when the query inputs change or the user hits Refresh.
  const [anchorMs, setAnchorMs] = useState(() => Date.now());
  const [tokenStack, setTokenStack] = useState([null]);
  const [idx, setIdx] = useState(0);

  // Reset DURING RENDER rather than in an effect.
  //
  // ⚠️ An effect here fired TWO CloudWatch requests for every window or keyword
  // change. `anchorMs` and `idx` are both in the query key, and this component
  // does not remount on those changes (only the group-keyed error boundary
  // remounts, and only on a group switch). So the render carrying the new
  // windowId but the OLD anchorMs/idx issued request #1 — with a nextToken from
  // the previous query, which is meaningless for the new parameters — and the
  // effect's state update then issued request #2. Doubled metered CloudWatch
  // calls and Lambda invocations on every keystroke-driven search.
  //
  // Adjusting state during render is React's documented answer for exactly this
  // (derive from changed props); the re-render happens before anything is
  // committed, so the query key is correct on the FIRST pass and only one
  // request goes out.
  const inputKey = `${groupId}|${windowId}|${filterPattern}`;
  const [prevInputKey, setPrevInputKey] = useState(inputKey);
  if (inputKey !== prevInputKey) {
    setPrevInputKey(inputKey);
    setAnchorMs(Date.now());
    setTokenStack([null]);
    setIdx(0);
  }

  const startTime = anchorMs - windowMs(windowId);
  const endTime = anchorMs;
  const nextToken = tokenStack[idx] ?? null;

  const { data, isFetching, error } = useQuery({
    queryKey: ["cwLogs", groupId, windowId, filterPattern, anchorMs, idx],
    queryFn: async () => {
      const body = {
        method: "cwLogs",
        groupId,
        startTime,
        endTime,
        filterPattern: filterPattern || "",
        limit: PAGE_LIMIT,
        ...(nextToken ? { nextToken } : {}),
      };
      const resp = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "x-api-key": MONGODB_AWSGW_APIKEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      return json?.message?.result ?? null;
    },
    keepPreviousData: true,
  });

  const serverError = data?.error || null;
  const rows = useMemo(() => {
    if (!data?.events) return [];
    return data.events.map((e) => {
      const p = parseCwLogEvent(e);
      return {
        ...p,
        __when: p.timestamp ? new Date(p.timestamp).toLocaleString() : "",
      };
    });
  }, [data]);

  const serverNextToken = data?.nextToken ?? null;

  const refresh = () => {
    setAnchorMs(Date.now());
    setTokenStack([null]);
    setIdx(0);
  };
  const goNext = () => {
    if (!serverNextToken) return;
    setTokenStack((stack) => {
      const next = stack.slice(0, idx + 1);
      next.push(serverNextToken);
      return next;
    });
    setIdx((i) => i + 1);
  };
  const goPrev = () => setIdx((i) => Math.max(0, i - 1));

  const btn = "px-2 py-1 border-2 border-skin-base rounded-sm disabled:opacity-40";

  return (
    <div className="flex flex-col h-full min-h-0">
      {serverError && (
        <div className="text-red text-xs mb-1">
          CloudWatch error: {serverError}
          {/AccessDenied/i.test(serverError)
            ? " — the queries-Lambda role needs the CloudWatch read policy attached."
            : ""}
        </div>
      )}
      <div className="flex-1 min-h-0 border-2 border-skin-base rounded-sm">
        <LogEventsTable
          rows={rows}
          columns={CW_COLUMNS}
          loading={isFetching && rows.length === 0}
          error={error ? String(error.message || error) : null}
          levelColor={cwLevelColor}
          getRowKey={(r, i) => r.id || String(i)}
          // When a line carries an embedded JSON payload, show the message
          // PREFIX above the pretty-printed JSON — not the whole raw line, which
          // would repeat that same JSON unformatted underneath it. (This read
          // `r.json ? r.raw : r.raw` — both branches identical, so the parsed
          // prefix `parseCwLogEvent` computes was never used.)
          getDetail={(r) => ({ json: r.json, text: r.json ? r.text : r.raw })}
        />
      </div>
      <div className="flex items-center gap-2 pt-2 text-xs flex-wrap">
        <button className={btn} onClick={goPrev} disabled={idx <= 0}>
          ◀ Prev
        </button>
        <span className="px-1">Page {idx + 1}</span>
        <button className={btn} onClick={goNext} disabled={!serverNextToken}>
          Next ▶
        </button>
        <button className={btn} onClick={refresh}>
          ⟳ Refresh
        </button>
        <span className="ml-auto text-skin-link">
          {rows.length} event{rows.length === 1 ? "" : "s"} on this page
          {isFetching ? " · loading…" : ""}
        </span>
      </div>
    </div>
  );
}

export default CwLogsBrowser;
